"use client";

import React from "react";
import { Clock, Activity, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const scenarios = [{
  id: "01",
  title: "UN & NGO Tender Supply",
  description: "Supporting compliant, reliable, and transparent procurement and delivery for institutional and development-sector buyers, including WASH, humanitarian, NFI, and emergency response supplies.",
  duration: "Global Reach",
  difficulty: "High Compliance",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678736/img/un_ngo_supply_crates.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678736/img/un_ngo_supply_crates.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678736/img/un_ngo_supply_crates.png-wvc-sizes",
    alt: "UN flagged supply crates in logistics hub"
  },
  link: "/BusinessDivisionPage"
}, {
  id: "02",
  title: "Solar Energy Solutions",
  description: "Sustainable power systems designed for remote industrial sites and agricultural projects. From high-capacity solar water pumps to off-grid power plants, we provide the engineering and hardware for green energy transition.",
  duration: "Sustainable",
  difficulty: "Engineering Led",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678736/img/solar_water_pump_system.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678736/img/solar_water_pump_system.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678736/img/solar_water_pump_system.png-wvc-sizes",
    alt: "Solar water pump system in rural area"
  },
  link: "/BusinessDivisionPage"
}, {
  id: "03",
  title: "Industrial & Mining Supply",
  description: "End-to-end logistics for the extractive industry. We represent global manufacturers to provide heavy machinery, spare parts, and specialized equipment required for large-scale mining operations across Africa and beyond.",
  duration: "Heavy Duty",
  difficulty: "Technical",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678736/img/mining_machinery_logistics.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678736/img/mining_machinery_logistics.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678736/img/mining_machinery_logistics.png-wvc-sizes",
    alt: "Heavy mining machinery being transported"
  },
  link: "/BusinessDivisionPage"
}, {
  id: "04",
  title: "WASH & Humanitarian Supply",
  description: "Specialized procurement and rapid deployment of Water, Sanitation, and Hygiene (WASH) equipment, non-food items (NFI), and emergency relief supplies for international aid organizations.",
  duration: "Rapid Response",
  difficulty: "Mission Critical",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678736/img/wash_humanitarian_supplies.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678736/img/wash_humanitarian_supplies.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678736/img/wash_humanitarian_supplies.png-wvc-sizes",
    alt: "Humanitarian relief supplies and WASH equipment"
  },
  link: "/BusinessDivisionPage"
}];
function ScenarioItem({
  scenario,
  index
}: {
  scenario: (typeof scenarios)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const isEven = index % 2 !== 0;
  return <div ref={ref} data-index={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`}>
      <div className={`relative ${isEven ? "lg:order-last" : ""}`}>
        <div className="relative rounded-lg overflow-hidden shadow-xl ring-1 ring-border/50 bg-muted">
          <img src={scenario.image.src} data-wvc-srcset={scenario.image.srcset} data-wvc-sizes={scenario.image.sizes} alt={scenario.image.alt} className="w-full h-auto object-cover aspect-[4/3] lg:aspect-[4/5] hover:scale-105 transition-transform duration-700" loading="lazy" />
        </div>
      </div>
      <div className="flex flex-col gap-6 lg:px-8">
        <span className="text-sm font-mono tracking-widest text-primary uppercase font-semibold">
          Capability {scenario.id}
        </span>
        <h3 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-secondary leading-tight">
          {scenario.title}
        </h3>
        <div className="flex flex-wrap gap-3">
          <Badge variant="secondary" className="gap-1.5 px-3 py-1.5 text-sm rounded-md bg-secondary text-secondary-foreground">
            <Clock className="w-4 h-4" />
            {scenario.duration}
          </Badge>
          <Badge variant="outline" className="gap-1.5 px-3 py-1.5 text-sm rounded-md bg-background border-primary text-primary">
            <Activity className="w-4 h-4" />
            {scenario.difficulty}
          </Badge>
        </div>
        <p className="text-lg text-muted-foreground font-default leading-relaxed">
          {scenario.description}
        </p>
        <div className="pt-4">
          <Button asChild size="lg" className="rounded-md group bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
            <Link to={scenario.link}>
              Explore Division
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function ReadyMadeWalkingScenarios() {
  const {
    headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="262982" id="strategic-supply-capabilities" className="py-24 lg:py-32 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-20 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold text-secondary mb-6 tracking-tight">
            Strategic Supply Capabilities
          </h2>
          <p className="text-xl text-muted-foreground font-default">
            AFREX Integrated Group delivers mission-critical supply chain solutions for global manufacturers, NGOs, and government entities across multiple continents.
          </p>
        </div>

        <div className="flex flex-col gap-24 lg:gap-32">
          {scenarios.map((scenario, i) => <ScenarioItem key={scenario.id} scenario={scenario} index={i} />)}
        </div>
      </div>
    </section>;
}